import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel, CircularProgress, Alert
} from '@mui/material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import HealthFormStep from './steps/HealthFormStep';
import PassDisplayStep from './steps/PassDisplayStep';
import { travelerScreeningAPI } from '../../services/api';
import { buildScreeningPayload } from './buildScreeningPayload';
import { C } from './formTheme';

/** PHO Add Client — same screening questions as WhatsApp Flow + temperature + phone. */
const STEPS = ['Health Screening', 'Pass'];

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const AddPassengerDialog: React.FC<Props> = ({ open, onClose, onSaved }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedScreening, setSavedScreening] = useState<Record<string, unknown> | null>(null);

  const handleFormChange = (field: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const cleaned = buildScreeningPayload(form);
      const result = await travelerScreeningAPI.createScreening(cleaned);
      // Pass-token is a separate call (not part of the create response) — the
      // QR code renders as "unavailable" rather than an insecure fallback
      // link if this fails, so it's fine to swallow errors here.
      try {
        const tokenData = await travelerScreeningAPI.getPassToken(result.id);
        if (tokenData?.token && result.pass_record) {
          result.pass_record.pass_token = tokenData.token;
        }
      } catch {
        /* non-fatal — screening is already saved */
      }
      setSavedScreening(result);
      setActiveStep(1);
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Failed to save screening. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setForm({});
    setError(null);
    setSavedScreening(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonAddAltIcon sx={{ color: C.primary }} />
        Add Client — Health Screening
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {activeStep === 0 && (
          <HealthFormStep
            form={form}
            onChange={handleFormChange}
            subTab={0}
            subTabLabels={[]}
            variant="agent"
          />
        )}
        {activeStep === 1 && savedScreening && (
          <PassDisplayStep screening={savedScreening} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{activeStep === 1 ? 'Close' : 'Cancel'}</Button>
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving ? 'Saving…' : 'Create Pass'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddPassengerDialog;
